class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.setupEventListeners();
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(event) {
		if (!this.getQuery().length) {
			event.preventDefault();
			return;
		}
		const selectedLink = this.querySelector('[aria-selected="true"] a');
		if (selectedLink) {
			event.preventDefault();
			selectedLink.click();
		}
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			this.showPromoBlock();
		}

		if (!currentSearchTerm.length) {
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			this.hidePromoBlock();
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close();
			});
		}
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.closeResults(true);
				break;
		}

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onKeydown(event) {
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
			this.navigateResults(event.code === "ArrowDown" ? 1 : -1);
		}
	}

	navigateResults(direction) {
		const options = this.querySelectorAll('[role="option"]');
		if (!options.length) return;

		const current = this.querySelector('[aria-selected="true"]');
		let nextIndex = -1;

		if (current) {
			const currentIndex = [...options].indexOf(current);
			current.setAttribute("aria-selected", "false");
			nextIndex = currentIndex + direction;
		} else {
			nextIndex = direction === 1 ? 0 : options.length - 1;
		}

		// Wrap around or return to input
		if (nextIndex < 0 || nextIndex >= options.length) {
			this.input.setAttribute("aria-activedescendant", "");
			return;
		}

		options[nextIndex].setAttribute("aria-selected", "true");
		this.input.setAttribute("aria-activedescendant", options[nextIndex].id);
		options[nextIndex].scrollIntoView({ block: "nearest" });
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText && previousTerm) {
			try {
				const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
				const matches = currentButtonText.match(new RegExp(escapedTerm, "g"));
				if (matches && matches.length > 1) {
					// The term appears multiple times in button text, skip to avoid mistakes
					return;
				}
			} catch (e) {
				// Skip replacement if regex fails
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replaceAll(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			return;
		}

		this.hidePromoBlock();

		this.abortController.abort();
		this.abortController = new AbortController();

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(
				searchTerm
			)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`,
			{ signal: this.abortController.signal }
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);
				this.renderSearchResults(resultsMarkup);
			})
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.textContent = statusText;

		clearTimeout(this.liveRegionTimer);
		this.liveRegionTimer = setTimeout(() => {
			this.statusElement.textContent = "";
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		this.hidePromoBlock();
	}

	get emptyState() {
		return document.querySelector(".search__empty-state");
	}

	hidePromoBlock() {
		if (this.emptyState) this.emptyState.classList.add("hide");
	}

	showPromoBlock() {
		if (this.emptyState) this.emptyState.classList.remove("hide");
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.querySelector("[data-predictive-search-live-region-count-value]")
				.textContent,
		);
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		const modal = this.closest('.search-modal');
		if (modal) modal.classList.add('predictive-open');
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		const modal = this.closest('.search-modal');
		if (modal) modal.classList.remove('predictive-open');
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		this.showPromoBlock();
	}
}

customElements.define("predictive-search", PredictiveSearch);